import React from 'react';
import { Select, Card, CardContent, Spinner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Check } from 'lucide-react';
import { COMMON_LOCALES, COMMON_TIMEZONES, COMMON_CURRENCIES } from './types';
import { formatDate, formatTime, setLocaleSettings, getLocaleSettings } from '@/lib/localeFormatters';
import { apiFetch } from '@/lib/api';
import { t } from '@/lib/i18n';

interface ThemePresetSummary {
  id: string;
  name: string;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  successColor: string | null;
  warningColor: string | null;
  errorColor: string | null;
  infoColor: string | null;
  grayColor: string | null;
  headingFontFamily: string | null;
  bodyFontFamily: string | null;
  borderRadius: string | null;
  darkModeEnabled: boolean;
}

const COLOR_KEYS = [
  'primaryColor', 'secondaryColor', 'accentColor',
  'successColor', 'warningColor', 'errorColor', 'infoColor', 'grayColor',
] as const;

interface LocaleTzData {
  locale: string;
  timezone: string;
  currency: string;
  themePresetId?: string;
  themePresetName?: string;
}

interface StepLocaleTzProps {
  value: LocaleTzData;
  onChange: (data: LocaleTzData) => void;
  autoFocus?: boolean;
}

export function StepLocaleTz({ value, onChange, autoFocus }: StepLocaleTzProps) {
  const localeSelectId = 'launchpad-locale-select';
  const [presets, setPresets] = React.useState<ThemePresetSummary[]>([]);
  const [presetsLoading, setPresetsLoading] = React.useState(true);

  // Auto-focus the first Select trigger on mount
  React.useEffect(() => {
    if (autoFocus) {
      requestAnimationFrame(() => {
        const trigger = document.getElementById(localeSelectId);
        trigger?.focus();
      });
    }
  }, [autoFocus]);

  // Auto-detect browser timezone on mount
  React.useEffect(() => {
    if (!value.timezone) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) {
          onChange({ ...value, timezone: tz });
        }
      } catch {
        // Fallback to default
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch theme presets
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/themes/presets');
        if (!cancelled && res.ok) {
          const json = await res.json();
          if (json.success && Array.isArray(json.data)) {
            setPresets(json.data);
            // Auto-select the first (default) preset if none selected
            if (!value.themePresetId && json.data.length > 0) {
              onChange({ ...value, themePresetId: json.data[0].id, themePresetName: json.data[0].name });
            }
            // If already selected, resolve name from fetched presets
            if (value.themePresetId && !value.themePresetName) {
              const match = json.data.find((p: ThemePresetSummary) => p.id === value.themePresetId);
              if (match) {
                onChange({ ...value, themePresetName: match.name });
              }
            }
          }
        }
      } catch {
        // Non-blocking
      } finally {
        if (!cancelled) setPresetsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field: keyof LocaleTzData, newValue: string) => {
    onChange({ ...value, [field]: newValue });
  };

  // Format preview — uses centralized locale formatters for date/time
  const formatPreview = React.useMemo(() => {
    try {
      const now = new Date();
      const numberFormatter = new Intl.NumberFormat(value.locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      const currencyFormatter = new Intl.NumberFormat(value.locale, {
        style: 'currency',
        currency: value.currency,
      });

      // Use centralized formatters that respect app locale settings (24h vs 12h, date format)
      const localeSettings = getLocaleSettings();

      return {
        number: numberFormatter.format(24890.5),
        currency: currencyFormatter.format(24890.5),
        date: formatDate(now) || localeSettings.dateFormat,
        time: formatTime(now) || localeSettings.timeFormat,
      };
    } catch {
      return {
        number: '24,890.50',
        currency: '€24,890.50',
        date: 'April 9, 2026',
        time: '14:30',
      };
    }
  }, [value.locale, value.timezone, value.currency]);

  const localeOptions = COMMON_LOCALES.map((l) => ({
    value: l.value,
    label: l.label,
  }));

  const timezoneOptions = COMMON_TIMEZONES.map((tz) => ({
    value: tz.value,
    label: tz.label,
  }));

  const currencyOptions = COMMON_CURRENCIES.map((c) => ({
    value: c.value,
    label: c.label,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">{t('Locale, Timezone & Theme')}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('Configure how dates, numbers, and currency are displayed, and choose a visual theme for your workspace.')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          label={t('Locale')}
          id={localeSelectId}
          options={localeOptions}
          value={value.locale}
          onValueChange={(val) => handleChange('locale', val)}
        />
        <Select
          label={t('Timezone')}
          options={timezoneOptions}
          value={value.timezone}
          onValueChange={(val) => handleChange('timezone', val)}
        />
        <Select
          label={t('Currency')}
          options={currencyOptions}
          value={value.currency}
          onValueChange={(val) => handleChange('currency', val)}
        />
      </div>

      {/* Live preview */}
      <div className="rounded-lg bg-muted border border-border p-4">
        <h3 className="text-sm font-medium text-foreground mb-3">{t('Formatting Preview')}</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">{t('Number')}:</span>
            <span className="ml-2 font-mono text-foreground">
              {formatPreview.number}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('Currency')}:</span>
            <span className="ml-2 font-mono text-foreground">
              {formatPreview.currency}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('Date')}:</span>
            <span className="ml-2 font-mono text-foreground">
              {formatPreview.date}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('Time')}:</span>
            <span className="ml-2 font-mono text-foreground">
              {formatPreview.time}
            </span>
          </div>
        </div>
      </div>

      {/* Theme Preset Selection */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-1">{t('Theme Preset')}</h3>
        <p className="text-xs text-muted-foreground mb-3">{t('Choose a visual theme for your workspace. Colors, fonts, and styling will be applied.')}</p>

        {presetsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
            <span className="ml-2 text-sm text-muted-foreground">{t('Loading themes...')}</span>
          </div>
        ) : presets.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">
            {t('No theme presets available. The default theme will be used.')}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {presets.map((preset) => (
              <ThemePresetOption
                key={preset.id}
                preset={preset}
                isSelected={value.themePresetId === preset.id}
                onClick={() => onChange({ ...value, themePresetId: preset.id, themePresetName: preset.name })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Theme Preset Option Card ───────────────────────────────────────────────

function ThemePresetOption({
  preset,
  isSelected,
  onClick,
}: {
  preset: ThemePresetSummary;
  isSelected: boolean;
  onClick: () => void;
}) {
  const isDefault = preset.id === 'default';

  return (
    // Use Button with layout-override classNames so the vertical card content
    // is preserved. flex+flex-col+items-start+whitespace-normal+h-auto override
    // the Button base's inline-flex/items-center/whitespace-nowrap/h-10.
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className={`flex flex-col items-start whitespace-normal h-auto relative text-left rounded-xl border p-4 w-full transition-all hover:shadow-md hover:text-current active:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        isSelected
          ? 'border-primary-500 ring-2 ring-primary-200 bg-primary-50/30 hover:bg-primary-50/30'
          : 'border-border bg-background hover:bg-background hover:border-primary-300'
      }`}
    >
      {/* Selected checkmark */}
      {isSelected && (
        <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center shadow-sm">
          <Check className="w-3.5 h-3.5 text-white" />
        </div>
      )}

      {/* Default badge */}
      {isDefault && (
        <div className="mb-2">
          <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-primary-500 text-white rounded-full">
            {t('Default')}
          </span>
        </div>
      )}

      <h4 className="text-sm font-semibold text-foreground mb-2.5 truncate pr-4">{preset.name}</h4>

      {/* Color swatches */}
      <div className="flex gap-1.5 mb-2.5 flex-wrap">
        {COLOR_KEYS.map((key) => {
          const color = preset[key] as string | null;
          return (
            <div
              key={key}
              className="w-5 h-5 rounded-full border border-black/10 shrink-0 bg-gray-200"
              style={color ? { backgroundColor: color } : undefined}
              title={`${key}: ${color || 'unset'}`}
            />
          );
        })}
      </div>

      {/* Font & radius info */}
      <div className="text-xs text-muted-foreground space-y-0.5">
        {preset.headingFontFamily && (
          <p><span className="font-medium">H:</span> {preset.headingFontFamily}</p>
        )}
        {preset.bodyFontFamily && (
          <p><span className="font-medium">B:</span> {preset.bodyFontFamily}</p>
        )}
        {preset.borderRadius && (
          <p><span className="font-medium">{t('Radius')}:</span> {preset.borderRadius}</p>
        )}
      </div>
    </Button>
  );
}
